import React, { useEffect } from "react";
import {
  AuthLayout,
  FormField,
  PasswordField,
  PrimaryButton,
  StatusAlert,
  LoginErrorAlert,
} from "@/components/auth";
import { useLogin, LOGIN_STATES } from "@/hooks/useLogin";
import { withAppBase } from "@/lib/appPaths";

export default function Login() {
  const {
    email,
    setEmail,
    password,
    setPassword,
    currentState,
    isSubmitting,
    isEmailValid,
    isFormValid,
    sessionExpired,
    isAuthenticated,
    navigate,
    handleSubmit,
  } = useLogin();

  useEffect(() => {
    if (isAuthenticated) {
      const next =
        new URLSearchParams(window.location.search).get("next") || "/";
      navigate(next, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  return (
    <AuthLayout
      footer={
        <div className="text-center space-y-2">
          <a
            href={withAppBase("/forgot-password")}
            data-testid="forgot-password-link"
            className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            Forgot password?
          </a>
          <p>
            <a
              href={withAppBase("/invite/accept")}
              data-testid="accept-invite-link"
              className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 transition-colors"
            >
              Have an invitation?{" "}
              <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                Accept invite
              </span>
            </a>
          </p>
        </div>
      }
    >
      <div data-testid="login-header" className="mb-6">
        <h2 className="text-xl font-bold font-heading text-slate-900 dark:text-slate-100 mb-2">
          Sign in
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Sign in to continue to the SmartStore Admin Console.
        </p>
      </div>

      {sessionExpired && (
        <div data-testid="session-expired-alert" className="mb-6">
          <StatusAlert
            variant="warning"
            title="Session expired"
            message="Your session has expired. Please sign in again to continue."
          />
        </div>
      )}
      {currentState !== LOGIN_STATES.DEFAULT && (
        <div data-testid="login-error-alert" className="mb-6">
          <LoginErrorAlert state={currentState} />
        </div>
      )}

      <form
        data-testid="login-form"
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <FormField
          id="email"
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="admin@company.com"
          autoComplete="email"
          error={
            email.length > 0 && !isEmailValid
              ? "Please enter a valid email address"
              : undefined
          }
        />

        <PasswordField
          id="password"
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
        />

        <div className="pt-2">
          <PrimaryButton
            data-testid="login-submit-btn"
            type="submit"
            disabled={!isFormValid}
            loading={isSubmitting}
          >
            Sign in
          </PrimaryButton>
        </div>
      </form>
    </AuthLayout>
  );
}
